import { forwardRef } from 'react';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/cn';
import type { ExploreCandidate } from '../../types';

export interface PluginCardProps {
  candidate: ExploreCandidate;
  selected: boolean;
  onSelect: (candidate: ExploreCandidate) => void;
  /** Hide the radio dot and show a "Selected" label — used when there is
   *  only one candidate in the picker, so the card reads as confirmation
   *  rather than as one option among several. */
  soloMode?: boolean;
  /** Tabindex management belongs to the parent radiogroup. */
  tabIndex?: number;
}

const PluginCard = forwardRef<HTMLDivElement, PluginCardProps>(function PluginCard(
  { candidate, selected, onSelect, soloMode = false, tabIndex = -1 },
  ref,
) {
  const title =
    candidate.displayName ||
    candidate.name ||
    (candidate.subdir ? candidate.subdir : '(repo root)');

  const metaParts: string[] = [];
  if (candidate.subdir) metaParts.push(candidate.subdir);
  else metaParts.push('(repo root)');
  if (candidate.license) metaParts.push(candidate.license);
  if (candidate.author?.name) metaParts.push(candidate.author.name);

  // tags is marketplace-only; keywords is the plugin-json equivalent.
  const chips = (candidate.tags ?? candidate.keywords ?? []).slice(0, 4);

  function handleClick() {
    onSelect(candidate);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onSelect(candidate);
    }
  }

  return (
    <div
      ref={ref}
      role="radio"
      aria-checked={selected}
      tabIndex={tabIndex}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'group cursor-pointer rounded-md border px-3 py-2.5 transition-colors focus-ring',
        selected
          ? 'border-foreground/70 bg-accent/30'
          : 'border-border hover:bg-accent/40',
      )}
    >
      <div className="flex items-start gap-3">
        {!soloMode && (
          <div className="mt-0.5 shrink-0">
            <span
              className={cn(
                'block h-4 w-4 rounded-full border',
                selected
                  ? 'border-foreground bg-foreground/80'
                  : 'border-muted-foreground/60 bg-transparent group-hover:border-foreground/60',
              )}
              aria-hidden="true"
            />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-medium text-foreground">
              {title}
            </span>
            <span className="ml-auto flex shrink-0 items-center gap-2">
              {candidate.version && (
                <span className="text-xs text-muted-foreground">
                  v{candidate.version}
                </span>
              )}
              <Badge variant={candidate.source === 'marketplace' ? 'info' : 'gray'}>
                {candidate.source}
              </Badge>
              {soloMode && (
                <span className="text-xs text-muted-foreground">Selected</span>
              )}
            </span>
          </div>

          {candidate.description && (
            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
              {candidate.description}
            </p>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            {metaParts.map((part, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span aria-hidden="true">·</span>}
                <span className={i === 0 ? 'font-mono' : ''}>{part}</span>
              </span>
            ))}
          </div>

          {chips.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {chips.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center rounded-full bg-muted/60 px-2 py-0.5 text-[10px] text-muted-foreground"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default PluginCard;
